import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { useSubmitContactForm } from '../hooks/useQueries';
import { toast } from 'sonner';
import { environment } from '@/env/env.prod';
import { getCaptchaToken } from '@/service/capcha.service';
import { submitContactForm } from '@/api/formSubmission';
import { CaptchaWidget } from '@/components/CapchaWidget';
import { FormUploadService } from '@/service/form.service';

export default function ConnectPage() {
  const [ scrollY, setScrollY ] = useState(0);

  const [ formData, setFormData ] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  // ✅ ADDED
  const [ isSubmitting, setIsSubmitting ] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  //const submitContactForm = useSubmitContactForm();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // ✅ ADDED (prevents multi-click spam)
    if (isSubmitting) return;

    if (!formData.name || !formData.email || !formData.phone || !formData.message) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      // ✅ ADDED
      setIsSubmitting(true);

      let formIns = new FormUploadService();
      await formIns.uploadContactForm(formData);

      toast.success("Message sent successfully!");
      setFormData({ name: "", email: "", phone: "", message: "" });

    } catch {
      toast.error("Failed to send message. Please try again.");
    } finally {
      // ✅ ADDED (guaranteed unlock)
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email',
      content: 'support@unilinkd.com',
      link: 'mailto:support@unilinkd.com',
    },
  ];

  return (
    <div className="flex flex-col ">
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-yellow via-brand-yellow to-[#FFE566] md:py-16 py-12 bg-brand-yellow py-12">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            transform: `translateY(${scrollY * 0.5}px)`,
            backgroundImage: 'radial-gradient(circle, #004CFF 1px, transparent 1px)',
            backgroundSize: '50px 50px',
          }}
        />
        <div className="container relative z-10">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="mb-4 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-5xl font-extrabold tracking-tight text-brand-black md:text-6xl">
              Connect With Us
            </h1>
            <p className="text-xl font-medium text-brand-black md:text-2xl">
              Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
            </p>
          </div>
        </div>
      </section>

      <section
        className="relative bg-brand-white py-12"
        style={{ transform: `translateY(${scrollY * 0.1}px)` }}
      >
        <div className="container">
          <div className="grid gap-10 lg:grid-cols-2 lg:gap-12">

            <div className="space-y-6">
              <div>
                <h2 className="mb-3 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-4xl font-extrabold tracking-tight text-brand-black">
                  Get in Touch
                </h2>
                <p className="text-xl font-medium text-brand-black">
                  Whether you have a question about features, pricing, need a demo, or anything else,
                  our team is ready to answer all your questions.
                </p>
              </div>

              <div className="space-y-4">
                {contactInfo.map((info, index) => (
                  <Card key={index} className="rounded-3xl bg-brand-white shadow-xl transition-all hover:scale-105 hover:shadow-2xl">
                    <CardHeader>
                      <div className="flex items-start space-x-4">
                        <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-blue to-[#0066FF] shadow-lg">
                          <info.icon className="h-6 w-6 text-brand-white" />
                        </div>
                        <div>
                          <CardTitle className="mb-1 text-lg font-bold text-brand-black">{info.title}</CardTitle>
                          <a href={info.link} className="font-medium text-brand-black hover:text-brand-blue">
                            {info.content}
                          </a>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            </div>

            <Card className="rounded-3xl bg-brand-white shadow-xl">
              <CardHeader>
                <CardTitle className="bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-2xl font-bold text-brand-black">
                  Send us a Message
                </CardTitle>
                <CardDescription className="text-base font-medium text-brand-black">
                  Fill out the form below and we'll get back to you within 24 hours
                </CardDescription>
              </CardHeader>

              <CardContent>
                <form className="space-y-5">

                  <div className="space-y-2">
                    <Label htmlFor="name" className="font-bold text-brand-black">Name</Label>
                    <Input
                      id="name"
                      placeholder="Your name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="rounded-xl font-medium shadow-md transition-all focus:shadow-lg"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="font-bold text-brand-black">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="rounded-xl font-medium shadow-md transition-all focus:shadow-lg"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="font-bold text-brand-black">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+1 (555) 123-4567"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      className="rounded-xl font-medium shadow-md transition-all focus:shadow-lg"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="font-bold text-brand-black">Message</Label>
                    <Textarea
                      id="message"
                      rows={6}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      className="rounded-xl font-medium shadow-md transition-all focus:shadow-lg"
                    />
                  </div>

                  <Button
                    type="button"
                    disabled={isSubmitting}  // ✅ ADDED
                    className="w-full rounded-xl bg-gradient-to-r from-brand-blue to-[#0066FF] text-lg font-bold text-brand-white shadow-xl transition-all hover:scale-105 hover:shadow-2xl disabled:opacity-50"
                    onClick={handleSubmit}
                  >
                    {isSubmitting ? (   // ✅ ADDED
                      <>
                        <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        Send Message
                      </>
                    )}
                  </Button>

                  <CaptchaWidget />
                </form>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>
    </div>
  );
}
